import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getTheme, type Theme, type ThemeColors } from '@/config/themes';
import { getHeaderFont } from '@/config/fonts';

interface ThemeContextValue {
  theme: Theme;
  themeId: string;
  setThemeId: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function usePublicTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('usePublicTheme must be used within a PublicThemeProvider');
  }
  return context;
}

interface PublicThemeProviderProps {
  themeId: string;
  headerFontId?: string;
  children: ReactNode;
}

// Store original CSS variable values to restore later
let originalValues: Record<string, string> = {};

function applyThemeToRoot(colors: ThemeColors) {
  const root = document.documentElement;
  
  // Save original values on first apply
  if (Object.keys(originalValues).length === 0) {
    const computedStyle = getComputedStyle(root);
    originalValues = {
      '--primary': computedStyle.getPropertyValue('--primary').trim(),
      '--primary-foreground': computedStyle.getPropertyValue('--primary-foreground').trim(),
      '--secondary': computedStyle.getPropertyValue('--secondary').trim(),
      '--secondary-foreground': computedStyle.getPropertyValue('--secondary-foreground').trim(),
      '--background': computedStyle.getPropertyValue('--background').trim(),
      '--foreground': computedStyle.getPropertyValue('--foreground').trim(),
      '--accent': computedStyle.getPropertyValue('--accent').trim(),
      '--accent-foreground': computedStyle.getPropertyValue('--accent-foreground').trim(),
      '--muted': computedStyle.getPropertyValue('--muted').trim(),
      '--muted-foreground': computedStyle.getPropertyValue('--muted-foreground').trim(),
      '--card': computedStyle.getPropertyValue('--card').trim(),
      '--card-foreground': computedStyle.getPropertyValue('--card-foreground').trim(),
      '--border': computedStyle.getPropertyValue('--border').trim(),
    };
  }
  
  // Apply theme colors to :root
  root.style.setProperty('--primary', colors.primary);
  root.style.setProperty('--primary-foreground', colors.primaryForeground);
  root.style.setProperty('--secondary', colors.secondary);
  root.style.setProperty('--secondary-foreground', colors.secondaryForeground);
  root.style.setProperty('--background', colors.background);
  root.style.setProperty('--foreground', colors.foreground);
  root.style.setProperty('--accent', colors.accent);
  root.style.setProperty('--accent-foreground', colors.accentForeground);
  root.style.setProperty('--muted', colors.muted);
  root.style.setProperty('--muted-foreground', colors.mutedForeground);
  root.style.setProperty('--card', colors.card);
  root.style.setProperty('--card-foreground', colors.cardForeground);
  root.style.setProperty('--border', colors.border);
  root.style.setProperty('--gradient-hero', `linear-gradient(135deg, hsl(${colors.primary}) 0%, hsl(${colors.secondary}) 100%)`);
}

function restoreOriginalTheme() {
  const root = document.documentElement;
  
  // Restore original values
  Object.entries(originalValues).forEach(([key, value]) => {
    if (value) {
      root.style.setProperty(key, value);
    } else {
      root.style.removeProperty(key);
    }
  });
  root.style.removeProperty('--gradient-hero');
  root.style.removeProperty('--font-display');
}

function loadHeaderFont(fontId: string) {
  const font = getHeaderFont(fontId);
  const linkId = `header-font-${font.id}`;
  
  // Load font if not already loaded
  if (!document.getElementById(linkId)) {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = font.googleFontsUrl;
    document.head.appendChild(link);
  }
  
  // Apply font family to :root
  document.documentElement.style.setProperty('--font-display', font.family);
}

export function PublicThemeProvider({ themeId, headerFontId = 'playfair-display', children }: PublicThemeProviderProps) {
  const [currentThemeId, setCurrentThemeId] = useState(themeId);
  const theme = getTheme(currentThemeId);

  useEffect(() => {
    setCurrentThemeId(themeId);
  }, [themeId]);

  useEffect(() => {
    // Apply theme to :root so all Tailwind classes pick it up
    applyThemeToRoot(theme.colors);
    
    // Load and apply header font
    loadHeaderFont(headerFontId);
    
    // Cleanup: restore original theme when unmounting
    return () => {
      restoreOriginalTheme();
    };
  }, [theme, headerFontId]);

  return (
    <ThemeContext.Provider value={{ theme, themeId: currentThemeId, setThemeId: setCurrentThemeId }}>
      {children}
    </ThemeContext.Provider>
  );
}
